import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  fetchAuthSession,
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (username: string, password: string, email: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  forceSignOut: () => Promise<void>; // Add this new method
  confirmSignUp: (username: string, code: string) => Promise<void>;
  resetPassword: (username: string) => Promise<void>;
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      
      // Check if we have valid credentials
      if (session.credentials && session.tokens?.idToken) {
        const idToken = session.tokens.idToken.toString();
        
        // Parse JWT payload
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000); // Fixed: divide by 1000, not 10000
        
        // Check if token is expired (use exp claim first)
        if (payload.exp && now > payload.exp) {
          // Token actually expired, force sign out
          await signOut();
          setUser(null);
          setIsAuthenticated(false);
          toast.error("Session expired. Please log in again.");
          navigate('/login');
          return;
        }

        // Custom 2-hour session limit from issued time
        if (payload.iat && now > payload.iat + (2 * 60 * 60)) {
          await signOut();
          setUser(null);
          setIsAuthenticated(false);
          toast.error("Session expired after 2 hours. Please log in again.");
          navigate('/login');
          return;
        }

        // Session is valid
        setUser(session.tokens.idToken.payload); // or however you want to structure user data
        setIsAuthenticated(true);
      } else {
        // No valid session
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Error checking auth state:", error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const _signIn = async (username: string, password: string) => {
    try {
      setLoading(true);
      
      // First, check if user is already signed in
      try {
        const existingSession = await fetchAuthSession();
        if (existingSession.credentials && existingSession.tokens?.idToken) {
          // User is already signed in, just update state and navigate
          await checkAuthState();
          toast.success("Already signed in!");
          navigate('/');
          return;
        }
      } catch (existingSessionError) {
        // No existing session, proceed with sign in
      }

      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (isSignedIn) {
        // Wait for auth state to be updated
        await checkAuthState();
        toast.success("Successfully signed in!");
        navigate('/');
      } else {
        throw new Error(`Sign in requires further steps: ${nextStep.signInStep}`);
      }
    } catch (error) {
      const err = error as Error;
      
      // Handle the specific "already signed in" error
      if (err.message.includes("already a signed in user")) {
        try {
          // Force sign out and try again
          await _forceSignOut();
          toast.info("Clearing previous session and retrying...");
          
          // Small delay to ensure cleanup
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Retry the sign in
          const { isSignedIn, nextStep } = await signIn({ username, password });
          if (isSignedIn) {
            await checkAuthState();
            toast.success("Successfully signed in!");
            navigate('/'); // Changed from '/dashboard' to '/'
          } else {
            throw new Error(`Sign in requires further steps: ${nextStep.signInStep}`);
          }
        } catch (retryError) {
          toast.error(`Sign in failed: ${(retryError as Error).message}`);
          throw retryError;
        }
      } else {
        toast.error(`Sign in failed: ${err.message}`);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const _signUp = async (username: string, password: string, email: string, name: string) => {
    try {
      setLoading(true);
      
      const { isSignUpComplete, nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            name
          },
        }
      });

      if (isSignUpComplete) {
        toast.success("Sign up successful and complete!");
        navigate('/login');
      } else {
        toast.success("Sign up successful! Please check your email for verification code.");
        navigate('/confirm-signup', { state: { username } });
      }
    } catch (error) {
      const err = error as Error;
      toast.error(`Sign up failed: ${err.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const _confirmSignUp = async (username: string, code: string) => {
    try {
      setLoading(true);
      
      await confirmSignUp({ username, confirmationCode: code });
      toast.success("Account verified successfully! Please sign in.");
      navigate('/login');
    } catch (error) {
      const err = error as Error;
      toast.error(`Verification failed: ${err.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const _signOut = async () => {
    try {
      setLoading(true);
      
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      toast.success("Successfully signed out!");
      navigate('/login');
    } catch (error) {
      const err = error as Error;
      toast.error(`Sign out failed: ${err.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Force sign out without showing errors - useful for clearing stuck sessions
  const _forceSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
    } catch (error) {
      // Silently handle errors during force sign out
      console.warn("Error during force sign out:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const _resetPassword = async (username: string) => {
    try {
      setLoading(true);
      
      await resetPassword({ username });
      toast.success("Reset code sent to your email!");
      navigate('/reset-password-confirm', { state: { username } });
    } catch (error) {
      const err = error as Error;
      toast.error(`Password reset failed: ${err.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const _confirmResetPassword = async (username: string, code: string, newPassword: string) => {
    try {
      setLoading(true);
      
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
      toast.success("Password reset successfully! Please sign in with your new password.");
      navigate('/login');
    } catch (error) {
      const err = error as Error;
      toast.error(`Password reset confirmation failed: ${err.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    signIn: _signIn,
    signUp: _signUp,
    signOut: _signOut,
    forceSignOut: _forceSignOut, // Add the new method
    confirmSignUp: _confirmSignUp,
    resetPassword: _resetPassword,
    confirmResetPassword: _confirmResetPassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};